/**
 * Modal — shared overlay + panel primitive used by every dialog in the
 * app. Matches the Studio aesthetic: rounded-3xl panel, white fill,
 * charcoal/10 hairline border, soft drop shadow, charcoal/40 scrim.
 *
 * Compose children freely — the primitive only renders the overlay
 * and the panel shell. The ModalHeader / ModalBody / ModalFooter
 * helpers are optional convenience pieces with the right default
 * spacing + dividers if you want them.
 *
 * Closes on Escape + backdrop click (with mousedown-on-panel guarded
 * so a click that starts inside the panel and drifts out doesn't
 * dismiss — matches native OS modal behavior).
 */
import {
  useEffect,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-6xl',
};

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Panel max-width. Default 'md'. */
  size?: ModalSize;
  /** ARIA label for the dialog (required for a11y). */
  ariaLabel?: string;
  /** Optional extra classes on the panel itself. */
  panelClassName?: string;
  children: ReactNode;
}

export function Modal({
  isOpen,
  onClose,
  size = 'md',
  ariaLabel,
  panelClassName,
  children,
}: ModalProps) {
  // Track where a mousedown started so drag-out doesn't dismiss the
  // modal. Native modals behave the same way — a click only dismisses
  // when it starts AND ends on the scrim.
  const downOnScrim = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const onScrimMouseDown = (e: ReactMouseEvent<HTMLDivElement>) => {
    downOnScrim.current = e.target === e.currentTarget;
  };
  const onScrimMouseUp = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (downOnScrim.current && e.target === e.currentTarget) {
      onClose();
    }
    downOnScrim.current = false;
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/40 p-4 backdrop-blur-[2px]"
      onMouseDown={onScrimMouseDown}
      onMouseUp={onScrimMouseUp}
    >
      <div
        className={
          'flex max-h-[85vh] w-full flex-col overflow-hidden rounded-3xl border border-charcoal/10 bg-white shadow-[0_24px_60px_rgba(43,42,47,0.22)] ' +
          SIZE_CLASS[size] +
          (panelClassName ? ' ' + panelClassName : '')
        }
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Standard modal header — pine eyebrow label + optional subtitle +
 * close X in the top-right. Matches the header rhythm of the admin
 * tabs, the New Meeting page header, etc.
 */
export function ModalHeader({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  onClose?: () => void;
  /** Extra content on the right side of the header (e.g. a filter control). */
  children?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-charcoal/10 px-6 py-4">
      <div className="min-w-0 flex-1">
        <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-pine">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-1 text-[12px] text-charcoal/70">{subtitle}</p>
        )}
      </div>
      {children}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="-mr-1 -mt-1 shrink-0 rounded-full p-1.5 text-charcoal/55 transition-colors hover:bg-cloud/60 hover:text-charcoal"
          aria-label="Close"
        >
          ✕
        </button>
      )}
    </div>
  );
}

/**
 * Scrollable body slot with default padding. Pass className to override
 * padding if you need full-bleed content (e.g. a list with its own
 * dividers).
 */
export function ModalBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={'flex-1 overflow-y-auto px-6 py-5 ' + (className ?? '')}
    >
      {children}
    </div>
  );
}

/**
 * Optional footer with a top divider. Good for action-button rows
 * (Cancel / Confirm) or for a small explanatory note.
 */
export function ModalFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={
        'border-t border-charcoal/10 bg-cloud/30 px-6 py-3 ' + (className ?? '')
      }
    >
      {children}
    </div>
  );
}
